import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { jobsAPI } from '../lib/api';
import { toast } from 'sonner';
import {
  ArrowLeft, ArrowRight, Sparkles, Wand2, ChevronDown, ChevronUp,
  FileText, ClipboardList, MessageSquareText, Rocket,
} from 'lucide-react';
import { StepRail, STEPS } from '../components/jdbuilder/StepRail';
import { SmartSuggestions } from '../components/jdbuilder/SmartSuggestions';
import { JDPreview } from '../components/jdbuilder/JDPreview';
import { ChipInput } from '../components/jdbuilder/ChipInput';
import { CURRENCIES, SALARY_PERIODS, formatSalary } from '../lib/salary';
const MODES = [
  { key: 'keywords', Icon: ClipboardList, title: 'Keywords', desc: 'Skills & role keywords', placeholder: 'Python, React, AWS, 5+ years, team lead, microservices, agile...' },
  { key: 'reference', Icon: FileText, title: 'Reference JD', desc: 'Paste an existing JD', placeholder: 'Paste the full job description from another company here...' },
  { key: 'situation', Icon: MessageSquareText, title: 'Situation', desc: 'Describe the hiring need', placeholder: "We need someone to build our mobile app from scratch. We're a fintech startup with 20 engineers who can lead a small team and ship fast..." },
];

const EMPTY_JD = {
  title: '', companyName: '', companyContext: '', overview: '',
  responsibilities: '', qualifications: '',
  requiredSkills: [], niceToHave: [],
  experienceYears: 0, location: 'Remote', jobType: 'full-time',
  salaryMin: '', salaryMax: '', salaryCurrency: 'USD', salaryPeriod: 'year',
};

const inputStyle = { width: '100%', padding: '0.6rem 0.85rem', borderRadius: 12, border: '1px solid #ECE7DE', background: '#fff', fontSize: '0.8125rem', color: '#002A38', outline: 'none', boxSizing: 'border-box' };
const labelStyle = { display: 'block', fontSize: '0.625rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#64748B', marginBottom: '0.375rem' };
const fieldWrap = { marginBottom: '1rem' };

export default function JDBuilderPage() {
  const navigate = useNavigate();
  const [jd, setJd] = useState(EMPTY_JD);
  const [activeStep, setActiveStep] = useState(1);
  const [mode, setMode] = useState('keywords');
  const [quickInput, setQuickInput] = useState('');
  const [quickOpen, setQuickOpen] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [creating, setCreating] = useState(false);

  const set = (patch) => setJd((prev) => ({ ...prev, ...patch }));
  const currentMode = MODES.find((m) => m.key === mode);

  const isComplete = (n) => {
    if (n === 1) return !!jd.title.trim();
    if (n === 2) return !!jd.overview.trim();
    if (n === 3) return !!jd.responsibilities.trim();
    if (n === 4) return jd.requiredSkills.length > 0;
    if (n === 5) return jd.niceToHave.length > 0 || !!jd.qualifications.trim();
    if (n === 6) return !!jd.title.trim() && jd.requiredSkills.length > 0;
    return false;
  };

  const suggestions = useMemo(() => {
    const list = [];
    // Content
    if (!jd.title.trim()) list.push({ id: 'title', category: 'content', tone: 'warning', text: 'Add a clear, specific job title', detail: 'Precise titles (e.g. "Senior Backend Engineer") attract more qualified applicants.' });
    else list.push({ id: 'title-ok', category: 'content', tone: 'success', text: 'Job title looks good' });
    if (jd.requiredSkills.length < 3) list.push({ id: 'skills', category: 'content', tone: 'warning', text: 'List at least 3–5 required skills', detail: 'Our AI uses these to score and rank candidates accurately.' });
    else list.push({ id: 'skills-ok', category: 'content', tone: 'success', text: `${jd.requiredSkills.length} required skills listed` });
    if (!jd.salaryMin && !jd.salaryMax) list.push({ id: 'salary', category: 'content', tone: 'info', text: 'Add a salary range', detail: 'Posts with pay ranges see up to 30% more applications — set it in step 1.' });
    else list.push({ id: 'salary-ok', category: 'content', tone: 'success', text: 'Salary range added' });
    // Structure
    if (!jd.overview.trim()) list.push({ id: 'overview', category: 'structure', tone: 'warning', text: 'Write a short role overview', detail: 'A 2–3 sentence summary gives candidates instant context.' });
    else if (jd.overview.trim().length < 120) list.push({ id: 'overview-thin', category: 'structure', tone: 'info', text: 'Expand the overview a little', detail: 'Aim for 2–3 sentences on mission, team and impact.' });
    else list.push({ id: 'overview-ok', category: 'structure', tone: 'success', text: 'Role overview added' });
    if (!jd.responsibilities.trim()) list.push({ id: 'resp', category: 'structure', tone: 'info', text: 'Break responsibilities into bullet points', detail: 'One duty per line — scannable lists convert better.' });
    else list.push({ id: 'resp-ok', category: 'structure', tone: 'success', text: 'Responsibilities added' });
    if (jd.niceToHave.length === 0) list.push({ id: 'nth', category: 'content', tone: 'info', text: 'Add a few nice-to-have skills', detail: "Signals what's a bonus vs a hard requirement." });
    return list;
  }, [jd]);

  const applyGenerated = (data) => {
    set({
      title: data.title || jd.title,
      overview: data.description || jd.overview,
      requiredSkills: Array.isArray(data.required_skills) && data.required_skills.length ? data.required_skills : jd.requiredSkills,
      niceToHave: Array.isArray(data.nice_to_have_skills) && data.nice_to_have_skills.length ? data.nice_to_have_skills : jd.niceToHave,
      experienceYears: typeof data.experience_years === 'number' ? data.experience_years : jd.experienceYears,
      location: data.location || jd.location,
      jobType: data.job_type || jd.jobType,
    });
  };

  const handleGenerate = async () => {
    let inputText = quickInput.trim();
    if (!inputText) {
      const parts = [jd.title, jd.requiredSkills.join(', '), jd.overview].filter(Boolean);
      inputText = parts.join('. ').trim();
    }
    if (!inputText) {
      toast.error('Add a title, some skills, or Quick Start keywords first');
      setQuickOpen(true);
      return;
    }
    setGenerating(true);
    try {
      const res = await jobsAPI.generateJD({ mode, input_text: inputText, company_name: jd.companyName, company_context: jd.companyContext });
      applyGenerated(res.data);
      toast.success('Job description drafted with AI');
      if (activeStep === 1) setActiveStep(2);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate JD');
    } finally { setGenerating(false); }
  };

  const composeDescription = () => [
    jd.overview.trim(),
    jd.responsibilities.trim() && `Key Responsibilities:\n${jd.responsibilities.trim()}`,
    jd.qualifications.trim() && `Preferred Qualifications:\n${jd.qualifications.trim()}`,
  ].filter(Boolean).join('\n\n');

  const handleCreateJob = async () => {
    if (!jd.title.trim()) { toast.error('A job title is required'); setActiveStep(1); return; }
    if (jd.requiredSkills.length === 0) { toast.error('Add at least one required skill'); setActiveStep(4); return; }
    if (jd.salaryMin && jd.salaryMax && parseInt(jd.salaryMin) > parseInt(jd.salaryMax)) { toast.error('Minimum salary cannot exceed the maximum'); setActiveStep(1); return; }
    setCreating(true);
    try {
      await jobsAPI.create({
        title: jd.title.trim(),
        description: composeDescription() || jd.title.trim(),
        required_skills: jd.requiredSkills,
        nice_to_have_skills: jd.niceToHave,
        experience_years: jd.experienceYears || 0,
        location: jd.location || 'Remote',
        job_type: jd.jobType || 'full-time',
        salary_min: jd.salaryMin ? parseInt(jd.salaryMin) : null,
        salary_max: jd.salaryMax ? parseInt(jd.salaryMax) : null,
        salary_currency: jd.salaryCurrency,
        salary_period: jd.salaryPeriod,
      });
      toast.success('Job created successfully!');
      navigate('/jobs');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create job');
    } finally { setCreating(false); }
  };

  const plainText = () => {
    const lines = [jd.title || 'Untitled Role'];
    if (jd.companyName) lines.push(jd.companyName);
    lines.push('', `${jd.jobType} · ${jd.location} · ${jd.experienceYears || 0}+ yrs`, '');
    const sal = formatSalary({ salary_min: jd.salaryMin, salary_max: jd.salaryMax, salary_currency: jd.salaryCurrency, salary_period: jd.salaryPeriod });
    if (sal) lines.push(`Salary: ${sal}`, '');
    if (jd.overview) lines.push(jd.overview, '');
    if (jd.responsibilities) lines.push('KEY RESPONSIBILITIES', jd.responsibilities, '');
    if (jd.requiredSkills.length) lines.push('REQUIRED SKILLS', jd.requiredSkills.join(', '), '');
    if (jd.niceToHave.length) lines.push('NICE TO HAVE', jd.niceToHave.join(', '), '');
    if (jd.qualifications) lines.push('PREFERRED QUALIFICATIONS', jd.qualifications, '');
    return lines.join('\n');
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(plainText()).then(() => toast.success('Copied to clipboard')).catch(() => toast.error('Could not copy'));
  };
  const handleDownload = () => {
    const blob = new Blob([plainText()], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${(jd.title || 'job-description').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')}.txt`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Downloaded JD');
  };
  const handleShare = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: jd.title || 'Job Description', text: plainText() }); return; } catch { /* cancelled */ }
    }
    handleCopy();
  };

  const goNext = () => setActiveStep((s) => Math.min(STEPS.length, s + 1));
  const goPrev = () => setActiveStep((s) => Math.max(1, s - 1));

  return (
    <div
      data-testid="jd-builder-page"
      style={{ margin: '-1.5rem -2rem -3rem', height: 'calc(100vh - 4px)', display: 'flex', flexDirection: 'column', overflow: 'hidden', background: '#F1EAE1', fontFamily: "'Manrope', 'Inter', sans-serif" }}
    >
      <div style={{ display: 'flex', flex: 1, minHeight: 0, width: '100%', overflow: 'hidden' }}>
        {/* LEFT — Step rail + assistant form */}
        <div style={{ width: 372, flexShrink: 0, display: 'flex', flexDirection: 'column', background: '#fff', borderRight: '1px solid #ECE7DE', boxShadow: '2px 0 10px rgba(15,23,42,0.02)', zIndex: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '0.9rem 1.25rem 0' }}>
            <button data-testid="jd-back-to-jobs" onClick={() => navigate('/jobs')} style={{ background: 'none', border: '1px solid #ECE7DE', borderRadius: 999, width: 34, height: 34, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#64748B', cursor: 'pointer' }}>
              <ArrowLeft size={16} />
            </button>
            <div>
              <div style={{ fontFamily: "'Cabinet Grotesk', sans-serif", fontSize: '1.05rem', fontWeight: 700, color: '#002A38', lineHeight: 1.1 }}>JD Builder</div>
              <div style={{ fontSize: '0.6875rem', color: '#94A3B8' }}>Guided, AI-assisted</div>
            </div>
          </div>

          <StepRail activeStep={activeStep} isComplete={isComplete} onGo={setActiveStep} />

          <div style={{ flex: 1, overflowY: 'auto', padding: '1rem 1.25rem 1.5rem' }} data-testid="jd-form-scroll">
            {/* Quick Start AI */}
            <div style={{ border: '1px solid #99F6E4', background: '#F0FDFA', borderRadius: 14, marginBottom: '1.25rem', overflow: 'hidden' }}>
              <button
                type="button"
                data-testid="quick-start-toggle"
                onClick={() => setQuickOpen((o) => !o)}
                style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 8, padding: '0.7rem 0.85rem', background: 'none', border: 'none', cursor: 'pointer' }}
              >
                <Wand2 size={16} color="#0D9488" />
                <span style={{ flex: 1, textAlign: 'left', fontFamily: "'Cabinet Grotesk', sans-serif", fontSize: '0.8125rem', fontWeight: 700, color: '#0F766E' }}>Quick Start with AI</span>
                {quickOpen ? <ChevronUp size={15} color="#0D9488" /> : <ChevronDown size={15} color="#0D9488" />}
              </button>
              {quickOpen && (
                <div style={{ padding: '0 0.85rem 0.85rem' }}>
                  <div style={{ display: 'flex', gap: 5, marginBottom: 8 }}>
                    {MODES.map((m) => {
                      const on = mode === m.key;
                      return (
                        <button
                          key={m.key}
                          type="button"
                          data-testid={`mode-${m.key}`}
                          onClick={() => setMode(m.key)}
                          style={{ flex: 1, padding: '0.5rem 0.35rem', borderRadius: 10, cursor: 'pointer', border: `1px solid ${on ? '#0D9488' : '#CCFBF1'}`, background: on ? '#fff' : 'transparent', textAlign: 'center', transition: 'background-color 0.15s, border-color 0.15s' }}
                        >
                          <m.Icon size={15} color={on ? '#0D9488' : '#5EEAD4'} style={{ margin: '0 auto 3px' }} />
                          <div style={{ fontSize: '0.6875rem', fontWeight: 700, color: on ? '#0F766E' : '#64748B' }}>{m.title}</div>
                        </button>
                      );
                    })}
                  </div>
                  <textarea
                    data-testid="quick-start-input"
                    value={quickInput}
                    onChange={(e) => setQuickInput(e.target.value)}
                    placeholder={currentMode.placeholder}
                    rows={mode === 'reference' ? 6 : 4}
                    style={{ ...inputStyle, resize: 'vertical', lineHeight: 1.5, background: '#fff', borderColor: '#CCFBF1' }}
                  />
                  <button
                    type="button"
                    data-testid="quick-start-generate"
                    onClick={handleGenerate}
                    disabled={generating}
                    style={{ marginTop: 8, width: '100%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '0.6rem', borderRadius: 999, border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: '0.8125rem', color: '#fff', background: generating ? '#94A3B8' : '#0D9488' }}
                  >
                    <Sparkles size={15} /> {generating ? 'Generating…' : 'Generate full JD'}
                  </button>
                </div>
              )}
            </div>

            {/* Active step form */}
            <StepForm step={activeStep} jd={jd} set={set} />

            {/* Nav */}
            <div style={{ display: 'flex', gap: 8, marginTop: '1.5rem' }}>
              <button
                type="button"
                data-testid="step-prev"
                onClick={goPrev}
                disabled={activeStep === 1}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '0.55rem 1rem', borderRadius: 999, border: '1px solid #ECE7DE', background: '#fff', color: '#475569', fontWeight: 600, fontSize: '0.8125rem', cursor: activeStep === 1 ? 'not-allowed' : 'pointer', opacity: activeStep === 1 ? 0.5 : 1 }}
              >
                <ArrowLeft size={14} /> Back
              </button>
              {activeStep < STEPS.length ? (
                <button
                  type="button"
                  data-testid="step-next"
                  onClick={goNext}
                  style={{ flex: 1, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 5, padding: '0.55rem 1rem', borderRadius: 999, border: 'none', background: '#0076A3', color: '#fff', fontWeight: 700, fontSize: '0.8125rem', cursor: 'pointer' }}
                >
                  Next <ArrowRight size={14} />
                </button>
              ) : (
                <button
                  type="button"
                  data-testid="step-create-job"
                  onClick={handleCreateJob}
                  disabled={creating}
                  style={{ flex: 1, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '0.55rem 1rem', borderRadius: 999, border: 'none', background: '#0076A3', color: '#fff', fontWeight: 700, fontSize: '0.8125rem', cursor: 'pointer' }}
                >
                  <Rocket size={14} /> {creating ? 'Creating…' : 'Create Job'}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* MIDDLE — Smart Suggestions */}
        <div style={{ width: 324, flexShrink: 0, background: '#FAF7F2', borderRight: '1px solid #ECE7DE', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          <SmartSuggestions suggestions={suggestions} />
        </div>

        {/* RIGHT — Live preview */}
        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <JDPreview
            jd={jd}
            generating={generating}
            creating={creating}
            onGenerate={handleGenerate}
            onDownload={handleDownload}
            onCopy={handleCopy}
            onShare={handleShare}
            onCreate={handleCreateJob}
          />
        </div>
      </div>
    </div>
  );
}

function StepForm({ step, jd, set }) {
  if (step === 1) {
    return (
      <div data-testid="step-content-1">
        <h2 style={{ fontFamily: "'Cabinet Grotesk', sans-serif", fontSize: '1rem', fontWeight: 700, color: '#002A38', marginBottom: '0.35rem' }}>Job Title & Basics</h2>
        <p style={{ fontSize: '0.75rem', color: '#94A3B8', marginBottom: '1.1rem' }}>Start with the essentials candidates scan first.</p>
        <div style={fieldWrap}>
          <label style={labelStyle}>Job Title</label>
          <input data-testid="field-title" value={jd.title} onChange={(e) => set({ title: e.target.value })} placeholder="e.g. Senior Backend Engineer" style={inputStyle} />
        </div>
        <div style={fieldWrap}>
          <label style={labelStyle}>Location</label>
          <input data-testid="field-location" value={jd.location} onChange={(e) => set({ location: e.target.value })} placeholder="Remote, Bengaluru, New York…" style={inputStyle} />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
          <div>
            <label style={labelStyle}>Job Type</label>
            <select data-testid="field-jobtype" value={jd.jobType} onChange={(e) => set({ jobType: e.target.value })} style={inputStyle}>
              <option value="full-time">Full-time</option>
              <option value="part-time">Part-time</option>
              <option value="contract">Contract</option>
              <option value="internship">Internship</option>
            </select>
          </div>
          <div>
            <label style={labelStyle}>Min. Experience (yrs)</label>
            <input data-testid="field-experience" type="number" min={0} value={jd.experienceYears} onChange={(e) => set({ experienceYears: parseInt(e.target.value) || 0 })} style={inputStyle} />
          </div>
        </div>
        <div style={{ marginTop: '1rem' }}>
          <label style={labelStyle}>Salary Range <span style={{ textTransform: 'none', fontWeight: 600, color: '#94A3B8', letterSpacing: 0 }}>(optional)</span></label>
          <div style={{ display: 'grid', gridTemplateColumns: '84px 1fr 1fr 92px', gap: '0.5rem' }}>
            <select data-testid="field-salary-currency" value={jd.salaryCurrency} onChange={(e) => set({ salaryCurrency: e.target.value })} style={inputStyle}>
              {CURRENCIES.map((c) => <option key={c.code} value={c.code}>{c.code}</option>)}
            </select>
            <input data-testid="field-salary-min" type="number" min={0} value={jd.salaryMin} onChange={(e) => set({ salaryMin: e.target.value })} placeholder="Min" style={inputStyle} />
            <input data-testid="field-salary-max" type="number" min={0} value={jd.salaryMax} onChange={(e) => set({ salaryMax: e.target.value })} placeholder="Max" style={inputStyle} />
            <select data-testid="field-salary-period" value={jd.salaryPeriod} onChange={(e) => set({ salaryPeriod: e.target.value })} style={inputStyle}>
              {SALARY_PERIODS.map((p) => <option key={p.value} value={p.value}>{p.short}</option>)}
            </select>
          </div>
        </div>
      </div>
    );
  }
  if (step === 2) {
    return (
      <div data-testid="step-content-2">
        <h2 style={{ fontFamily: "'Cabinet Grotesk', sans-serif", fontSize: '1rem', fontWeight: 700, color: '#002A38', marginBottom: '0.35rem' }}>Company & Overview</h2>
        <p style={{ fontSize: '0.75rem', color: '#94A3B8', marginBottom: '1.1rem' }}>Set the scene — who you are and what the role is about.</p>
        <div style={fieldWrap}>
          <label style={labelStyle}>Company Name</label>
          <input data-testid="field-company" value={jd.companyName} onChange={(e) => set({ companyName: e.target.value })} placeholder="Optional" style={inputStyle} />
        </div>
        <div style={fieldWrap}>
          <label style={labelStyle}>Company Context</label>
          <input data-testid="field-company-context" value={jd.companyContext} onChange={(e) => set({ companyContext: e.target.value })} placeholder="Industry, size, culture…" style={inputStyle} />
        </div>
        <div style={fieldWrap}>
          <label style={labelStyle}>Role Overview</label>
          <textarea data-testid="field-overview" value={jd.overview} onChange={(e) => set({ overview: e.target.value })} rows={7} placeholder="A 2–3 sentence summary of the mission, team and impact of this role." style={{ ...inputStyle, resize: 'vertical', lineHeight: 1.6 }} />
        </div>
      </div>
    );
  }
  if (step === 3) {
    return (
      <div data-testid="step-content-3">
        <h2 style={{ fontFamily: "'Cabinet Grotesk', sans-serif", fontSize: '1rem', fontWeight: 700, color: '#002A38', marginBottom: '0.35rem' }}>Key Responsibilities</h2>
        <p style={{ fontSize: '0.75rem', color: '#94A3B8', marginBottom: '1.1rem' }}>One responsibility per line — these render as bullets.</p>
        <div style={fieldWrap}>
          <label style={labelStyle}>Responsibilities</label>
          <textarea data-testid="field-responsibilities" value={jd.responsibilities} onChange={(e) => set({ responsibilities: e.target.value })} rows={10} placeholder={'Design and ship backend services\nCollaborate with product on new features\nMentor junior engineers'} style={{ ...inputStyle, resize: 'vertical', lineHeight: 1.6 }} />
        </div>
      </div>
    );
  }
  if (step === 4) {
    return (
      <div data-testid="step-content-4">
        <h2 style={{ fontFamily: "'Cabinet Grotesk', sans-serif", fontSize: '1rem', fontWeight: 700, color: '#002A38', marginBottom: '0.35rem' }}>Requirements</h2>
        <p style={{ fontSize: '0.75rem', color: '#94A3B8', marginBottom: '1.1rem' }}>Must-have skills the AI uses to score candidates.</p>
        <div style={fieldWrap}>
          <label style={labelStyle}>Required Skills</label>
          <ChipInput value={jd.requiredSkills} onChange={(v) => set({ requiredSkills: v })} placeholder="Type a skill and press Enter" color="#0076A3" testid="required-skills-input" />
        </div>
      </div>
    );
  }
  if (step === 5) {
    return (
      <div data-testid="step-content-5">
        <h2 style={{ fontFamily: "'Cabinet Grotesk', sans-serif", fontSize: '1rem', fontWeight: 700, color: '#002A38', marginBottom: '0.35rem' }}>Qualifications</h2>
        <p style={{ fontSize: '0.75rem', color: '#94A3B8', marginBottom: '1.1rem' }}>Bonus skills and preferred background.</p>
        <div style={fieldWrap}>
          <label style={labelStyle}>Nice-to-have Skills</label>
          <ChipInput value={jd.niceToHave} onChange={(v) => set({ niceToHave: v })} placeholder="Type a skill and press Enter" color="#0D9488" testid="nice-to-have-input" />
        </div>
        <div style={fieldWrap}>
          <label style={labelStyle}>Preferred Qualifications</label>
          <textarea data-testid="field-qualifications" value={jd.qualifications} onChange={(e) => set({ qualifications: e.target.value })} rows={6} placeholder={"Bachelor's in CS or equivalent\nExperience in a high-growth startup"} style={{ ...inputStyle, resize: 'vertical', lineHeight: 1.6 }} />
        </div>
      </div>
    );
  }
  // step 6 — Complete
  const ready = !!jd.title.trim() && jd.requiredSkills.length > 0;
  return (
    <div data-testid="step-content-6">
      <h2 style={{ fontFamily: "'Cabinet Grotesk', sans-serif", fontSize: '1rem', fontWeight: 700, color: '#002A38', marginBottom: '0.35rem' }}>Review & Create</h2>
      <p style={{ fontSize: '0.75rem', color: '#94A3B8', marginBottom: '1.1rem' }}>Check the live preview on the right, then publish.</p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {[
          ['Title', jd.title || '—'],
          ['Location', jd.location || '—'],
          ['Type', jd.jobType],
          ['Experience', `${jd.experienceYears || 0}+ yrs`],
          ['Salary', formatSalary({ salary_min: jd.salaryMin, salary_max: jd.salaryMax, salary_currency: jd.salaryCurrency, salary_period: jd.salaryPeriod }) || 'Not posted'],
          ['Required skills', jd.requiredSkills.length ? `${jd.requiredSkills.length}` : '0'],
          ['Nice-to-have', jd.niceToHave.length ? `${jd.niceToHave.length}` : '0'],
        ].map(([k, v]) => (
          <div key={k} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8125rem', padding: '0.5rem 0.75rem', borderRadius: 10, background: '#FAF7F2', border: '1px solid #EEF2F7' }}>
            <span style={{ color: '#64748B', fontWeight: 600 }}>{k}</span>
            <span style={{ color: '#002A38', fontWeight: 700, textTransform: k === 'Type' ? 'capitalize' : 'none' }}>{v}</span>
          </div>
        ))}
      </div>
      {!ready && (
        <div style={{ marginTop: '1rem', fontSize: '0.75rem', color: '#B45309', background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 10, padding: '0.6rem 0.75rem' }}>
          Add a job title and at least one required skill to create this job.
        </div>
      )}
    </div>
  );
}
